import { computed, signal } from '@lit-labs/signals';
import { connectFirestoreToEmulator, getFirestoreDb, } from '@df/firebase';
import { FirestoreCollectionStore } from './firestore-base.store.js';
import { Timestamp, collection, orderBy, } from 'firebase/firestore';
import { firebaseAuthState } from './firebase-auth.store.js';
const FIRESTORE_HOST = '127.0.0.1';
const FIRESTORE_PORT = 8280;
const COLLECTION_PATH = 'chatMessage';
const DEFAULT_PAGE_SIZE = 50;
const fallbackStateSignal = signal({
    status: 'idle',
    documents: [],
    error: null,
    isListening: false,
    lastUpdated: null,
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasNextPage: false,
    hasPreviousPage: false,
    queryDescription: 'Latest messages',
});
const sendStatusSignal = signal('idle');
const sendErrorSignal = signal(null);
let collectionRef = null;
const storeSignal = signal(null);
export const chatMessagesState = computed(() => {
    const store = storeSignal.get();
    return store ? store.state.get() : fallbackStateSignal.get();
});
export const chatSendState = computed(() => ({
    status: sendStatusSignal.get(),
    error: sendErrorSignal.get(),
}));
export async function initializeChatStore(app, useEmulator) {
    if (storeSignal.get()) {
        return;
    }
    const db = getFirestoreDb(app);
    if (useEmulator) {
        connectFirestoreToEmulator(db, {
            host: FIRESTORE_HOST,
            port: FIRESTORE_PORT,
        });
    }
    // Offline persistence disabled for chat - we want fresh data on every load
    // and real-time updates are more important than offline viewing for chat
    // await enableFirestoreOfflinePersistence(db);
    collectionRef = collection(db, COLLECTION_PATH);
    const newStore = new FirestoreCollectionStore(collectionRef, {
        defaultConstraints: [orderBy('createdAt', 'asc')],
        defaultQueryDescription: 'Messages (oldest first)',
        pageSize: DEFAULT_PAGE_SIZE,
        transformCreate: applyCreateTransforms,
        mapDocument: normalizeFromFirestore,
    });
    storeSignal.set(newStore);
    await newStore.loadInitialPage();
    newStore.startRealtime();
}
export async function sendChatMessage(draft) {
    const trimmed = draft.text.trim();
    if (!trimmed) {
        throw new Error('Cannot send an empty message.');
    }
    const activeStore = ensureStore();
    const { authUser } = firebaseAuthState.get();
    if (!authUser) {
        throw new Error('You must be signed in to send messages.');
    }
    sendStatusSignal.set('sending');
    sendErrorSignal.set(null);
    const payload = {
        text: trimmed,
        userId: authUser.uid,
        userDisplayName: resolveDisplayName(authUser.displayName, authUser.email),
        userPhotoURL: authUser.photoURL ?? null,
        createdAt: new Date(),
    };
    try {
        const id = await activeStore.create(payload);
        sendStatusSignal.set('idle');
        return id;
    }
    catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to send chat message.';
        sendStatusSignal.set('error');
        sendErrorSignal.set(message);
        throw error;
    }
}
export function startChatRealtime() {
    ensureStore().startRealtime();
}
export function stopChatRealtime() {
    ensureStore().stopRealtime();
}
export function resetChatDemoState() {
    storeSignal.set(null);
    collectionRef = null;
    sendStatusSignal.set('idle');
    sendErrorSignal.set(null);
    fallbackStateSignal.set({
        status: 'idle',
        documents: [],
        error: null,
        isListening: false,
        lastUpdated: null,
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        hasNextPage: false,
        hasPreviousPage: false,
        queryDescription: 'Latest messages',
    });
}
export function __setChatDemoState(state) {
    if (storeSignal.get()) {
        throw new Error('Cannot set chat demo state after the chat store has been initialized.');
    }
    fallbackStateSignal.set(state);
}
export function __setChatSendState(status, error = null) {
    if (storeSignal.get()) {
        throw new Error('Cannot override chat send state after initialization.');
    }
    sendStatusSignal.set(status);
    sendErrorSignal.set(error);
}
function ensureStore() {
    const store = storeSignal.get();
    if (!store || !collectionRef) {
        throw new Error('Chat store has not been initialized. Call initializeChatStore() first.');
    }
    return store;
}
function resolveDisplayName(displayName, email) {
    if (displayName && displayName.trim()) {
        return displayName.trim();
    }
    if (email && email.trim()) {
        const [localPart] = email.split('@');
        return localPart;
    }
    return 'Anonymous';
}
function applyCreateTransforms(data) {
    return {
        ...data,
        createdAt: data.createdAt ?? new Date(),
    };
}
function normalizeFromFirestore(doc) {
    const createdAt = doc.createdAt instanceof Timestamp ? doc.createdAt.toDate() : doc.createdAt ?? null;
    return {
        ...doc,
        createdAt,
    };
}
//# sourceMappingURL=chat.store.js.map